import { Component, ElementRef, HostListener, computed, effect, inject, linkedSignal, signal, viewChildren } from '@angular/core';
import { DataService, InternationalizationService, SessionService, UmbracoService } from '@app/core/services';

import { AnimationEvent } from '@angular/animations';
import { UpperCasePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { environment } from '@app/environment';
import { CountryViewModel } from '@app/shared/rest-api';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation } from 'angular-animations';
import { UserDetailsComponent } from '../user-details/user-details.component';

@Component({
    selector: 'app-select-language',
    templateUrl: './select-language.component.html',
    styleUrls: ['./select-language.component.scss'],
    animations: [
        fadeInOnEnterAnimation(),
        fadeOutOnLeaveAnimation(),
    ],
    imports: [FormsModule, UserDetailsComponent, UpperCasePipe]
})
export class SelectLanguageComponent {
  private readonly selectedCenterOffsetPx = 50;

  #ds = inject(DataService);
  #umbracoService = inject(UmbracoService);
  #sessionService = inject(SessionService);

  i18n = inject(InternationalizationService);

  langElements = viewChildren<ElementRef<HTMLElement>>('lang');

  languages = toSignal(this.#umbracoService.getLanguages(), { initialValue: [] });
  langIndex = linkedSignal(() => this.languages()?.findIndex(el => el.isoCode4Letters === environment.defaultLanguage) ?? 0);
  langSelected = computed(() => this.languages()[this.langIndex()] ?? null);

  showLanguageSelect = true;
  showCountrySelect = false;
  showUserDetails = false;

  countryInput = signal('');
  countrySelected = signal<CountryViewModel | null>(null);
  filteredCountries = computed(() => {
    const query = this.countryInput();
    return (query.length > 1) ? this.#ds.searchCountries(query) : [];
  });

  fadeInDuration = 1000;
  fadeOutDuration = 1000;

  constructor() {
    effect(() => {
      this.centerSelectedLanguage();
    });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.centerSelectedLanguage();
  }

  private centerSelectedLanguage() {
    const elements = this.langElements();
    const selectedElement = elements[this.langIndex()]?.nativeElement;
    const wrapper = selectedElement?.parentElement;

    if (!selectedElement || !wrapper || wrapper.clientWidth === 0) {
      return;
    }

    const selectedCenter = selectedElement.offsetLeft + selectedElement.offsetWidth / 2;
    const wrapperCenter = wrapper.clientWidth / 2;
    const translateX = wrapperCenter - selectedCenter + this.selectedCenterOffsetPx;

    elements.forEach(el => {
      el.nativeElement.style.transform = `translateX(${translateX}px)`;
    });
  }

  skipLanguage(ev: Event, orientation: string) {
    ev.preventDefault();
    if (orientation === 'left') {
      this.langIndex.update((index) => Math.max(0, index - 1));
    } else {
      this.langIndex.update((index) => Math.min(this.languages().length - 1, index + 1));
    }
  }

  confirmLanguage(ev: Event) {
    ev.preventDefault();

    this.showLanguageSelect = false;

    const location = this.#sessionService.getLocation();
    this.#sessionService.setLocation({
      ...location,
      language: this.langSelected(),
    });

    this.#umbracoService.setLanguage(this.langSelected()?.isoCode4Letters);

     if(!['en', 'fr'].includes(this.langSelected()?.isoCode2Letters)) {
       document.documentElement.style.setProperty(`--main-font`, "var(--special-font)");
     } else {
       document.documentElement.style.setProperty(`--main-font`, "var(--default-font)");
     }
  }

  pickLanguage(index: number) {
    this.langIndex.set(index);
  }

  showCountryForm(ev: AnimationEvent) {
    if(ev.fromState !== 'void') {
      this.showCountrySelect = true;
    }
  }

  selectCountry(ev: MouseEvent | null, country: CountryViewModel) {
    ev?.preventDefault();
    this.countrySelected.set(country);
    this.countryInput.set('');
  }

  confirmCountry(ev: Event) {
    ev.preventDefault();

    const location = {
      language: this.langSelected(), 
      country: this.countrySelected(),
    };

    this.showCountrySelect = false;    
    this.#sessionService.setLocation(location);
  }

  showUserDetailsForm(ev: AnimationEvent) {
    if (ev.fromState !== 'void') {
      this.showUserDetails = true;
    }
  }
}
